<template>
  <el-dropdown
    v-if="visible"
    trigger="click"
    @command="handleActions"
    :loading="loading"
  >
    <el-button type="primary">
      {{ $t('Profiles_Button_Actions') }}
      <i class="el-icon-arrow-down el-icon--right" />
    </el-button>
    <el-dropdown-menu slot="dropdown">
      <el-dropdown-item
        command="deleteData"
        v-if="!$isRole('consent-admin') && $can('UserPersonalData::Delete')"
      >
        <i class="xp-icon-person-delete" />
        {{ $t('Profiles_Button_DeletePersonalData') }}
      </el-dropdown-item>
      <el-dropdown-item
        command="download"
        v-if="$can('UserPersonalData::Export')"
      >
        <i class="el-icon-download" />
        {{ $t('Profiles_Button_Download') }}
      </el-dropdown-item>
      <el-dropdown-item
        v-if="checkSeedListIsAvailable"
        command="toggleSeedList"
        :icon="inSeedList ? 'el-icon-close' : 'el-icon-plus'"
      >
        {{
          inSeedList
            ? $t('UserProfiles_ProfileList_RemoveFromSeedList')
            : $t('UserProfiles_ProfileList_AddToSeedList')
        }}
      </el-dropdown-item>
      <el-dropdown-item v-if="checkActivityAuditLink">
        <el-link
          :href="workflowActivityAuditLink"
          icon="el-icon-data-line"
          :underline="false"
          target="_blank"
        >
          {{ $t('Profiles_Button_WorkflowActivityAudit') }}
        </el-link>
      </el-dropdown-item>
      <el-dropdown-item
        v-if="$can('Profile::Delete')"
        command="deleteUser"
        data-qa="delete-user-btn"
      >
        <i class="el-icon-delete" />
        {{ $t('Profiles_Button_DeleteUser') }}
      </el-dropdown-item>
    </el-dropdown-menu>
  </el-dropdown>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { projectEndpoint, projectRoute, customConfirm } from '@/core/helpers'
import { $isSuperAdmin, $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'ProfileActionsDropdown',

  props: {
    profileId: {
      required: true
    },
    projectId: {
      required: true
    },
    inSeedList: {
      required: false,
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      mxaDomain: this.$store.getters.mxaDomain
    }
  },

  computed: {
    visible() {
      return (
        this.$can('UserPersonalData::Export') ||
        this.$can('UserPersonalData::Delete') ||
        this.$can('Profile::Delete')
      )
    },

    deleteEndpoint() {
      return projectEndpoint({
        method: 'delete',
        url: `user-profiles/${this.profileId}`
      })
    },

    deletePersonalDataEndpoint() {
      return projectEndpoint({
        method: 'delete',
        url: `user-profiles/${this.profileId}/clear`
      })
    },

    exportEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `user-profiles/export`,
        params: { id: this.profileId }
      })
    },

    workflowActivityAuditLink() {
      return `https://${this.mxaDomain}/project/${this.projectId}/admin/profiles/${this.profileId}/activity`
    },

    checkActivityAuditLink() {
      return $hasFeature('maxautomation') && $isSuperAdmin()
    },

    rowToggleSeedListEndpoint() {
      if (this.inSeedList) {
        return projectEndpoint({
          method: 'delete',
          url: `seed-list/${this.profileId}`
        })
      }

      return projectEndpoint({
        method: 'post',
        url: `seed-list`,
        data: {
          profile_id: this.profileId
        }
      })
    },

    checkSeedListIsAvailable() {
      return (
        this.$can('manageProjectTechnical') &&
        $hasFeature('featureSeedList') &&
        $hasFeature('seedListEnabled')
      )
    }
  },

  methods: {
    handleActions(action) {
      switch (action) {
        case 'deleteUser':
          this.confirmDelete()
          break
        case 'deleteData':
          this.confirmDeletePersonalData()
          break
        case 'download':
          this.downloadAction()
          break
        case 'toggleSeedList':
          let message = this.inSeedList
            ? 'UserProfiles_SeedList_ConfirmRemove'
            : 'UserProfiles_SeedList_ConfirmAdd'
          customConfirm(
            '',
            this.$t(message),
            this.$t('UserProfiles_Text_Yes'),
            this.$t('UserProfiles_Text_No')
          )
            .then(() => {
              RestApi(this.rowToggleSeedListEndpoint).then(() => {
                this.$emit('refreshModel')
              })
            })
            .catch(() => {})
          break
      }
    },

    downloadAction() {
      RestApi(this.exportEndpoint)
        .then(({ data }) => {
          this.$router.push(
            projectRoute({
              name: 'TaskHistoryView',
              query: { id: data }
            })
          )
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    confirmDeletePersonalData() {
      this.$msgbox({
        title: this.$t('Profiles_DialogDeletePersonalData_Title'),
        message: this.$t('Profiles_DialogDeletePersonalData_Prompt'),
        showCancelButton: true,
        confirmButtonText: this.$t('Profiles_Button_Confirm'),
        cancelButtonText: this.$t('Profiles_Button_Cancel'),
        center: true,
        dangerouslyUseHTMLString: true
      }).then(() => {
        this.loading = true
        RestApi(this.deletePersonalDataEndpoint)
          .then(({ data }) => {
            this.$router.push(
              projectRoute({
                name: 'TaskHistoryView',
                query: { id: data }
              })
            )
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.loading = false
          })
      })
    },

    confirmDelete() {
      this.$msgbox({
        title: this.$t('Profiles_DialogDelete_Title'),
        message: this.$t('Profiles_DialogDelete_Prompt'),
        showCancelButton: true,
        confirmButtonText: this.$t('Profiles_Button_Confirm'),
        cancelButtonText: this.$t('Profiles_Button_Cancel'),
        center: true
      }).then(() => {
        this.loading = true
        RestApi(this.deleteEndpoint)
          .then(() => {
            this.$message({
              message: this.$t('Profiles_Message_DeleteSuccess'),
              type: 'success'
            })

            this.loading = false

            this.$router.push(
              projectRoute({
                name: 'Profiles'
              })
            )
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.loading = false
          })
      })
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/profiles/components/ProfileActionsDropdown.vue